// 12 random bytes are used to create the IV
const IV_LENGTH = 12;

async function generateInitializationVector(crypto: Crypto): Promise<Uint8Array> {
    return crypto.getRandomValues(new Uint8Array(IV_LENGTH));
}

// TODO: This utility is also used in the Service Worker
// Consider a mechanism to share code between the worker and ClientApp compilations.
// Combines two array buffers into one, with buffer1 placed at the beginning of the returned buffer, followed by buffer2
function appendBuffer(buffer1: ArrayBuffer, buffer2: ArrayBuffer) {
    const temp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
    temp.set(new Uint8Array(buffer1), 0);
    temp.set(new Uint8Array(buffer2), buffer1.byteLength);
    return temp.buffer as ArrayBuffer;
}

export async function encryptData<T>(
    toEncrypt: T,
    key: CryptoKey,
    webCrypto: Crypto,
    encode: (toEncode: string) => Promise<Uint8Array>
): Promise<ArrayBuffer> {
    const encodedResponse = await encode(JSON.stringify(toEncrypt));

    // NOTE: Use a new IV per-encryption.
    // For AES-GCM encryption, it is not required to keep the IV secret, but it is required to keep the IV different for each encryption
    const iv = await generateInitializationVector(webCrypto);

    // We create the IV, and prepend to the encrypted text.
    // The stored result in cache will be:
    // First 12 bytes: the IV
    // The remaining bytes: a `JSON.stringify` result of type `T`
    const encryptedBody = await webCrypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv,
            tagLength: 128
        },
        key,
        encodedResponse
    );

    return appendBuffer(iv.buffer as ArrayBuffer, encryptedBody);
}

export async function decryptData<T>(
    toDecryptPlusIv: Uint8Array,
    key: CryptoKey,
    webCrypto: Crypto,
    decode: (toDecode: Uint8Array) => Promise<string>,
    reviver?: (key: string, value: any) => any
): Promise<T> {
    // The first 12 bytes are the IV
    const iv = toDecryptPlusIv.slice(0, IV_LENGTH);

    // The remaining bytes are the encrypted, text-encoded 'T' stored as the result of `JSON.stringify`
    const toDecrypt = toDecryptPlusIv.slice(IV_LENGTH, toDecryptPlusIv.byteLength);

    const decryptedResponse = await webCrypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv,
            tagLength: 128
        },
        key,
        toDecrypt
    );

    const decodedResponse = await decode(new Uint8Array(decryptedResponse));

    return JSON.parse(decodedResponse, reviver) as T;
}
